/**
 * Portfolio Optimizer — client-side correlation & allocation engine.
 *
 * Computes:
 *  1. Pearson correlation matrix from aligned daily return series
 *  2. Covariance matrix (annualised)
 *  3. Max-Sharpe, Min-Variance, and Min-MaxDD portfolio optimisations
 */

// ─── Types ────────────────────────────────────────────────────

export type OptimizationMode = "max_sharpe" | "max_cagr" | "min_drawdown";

export interface EquityCurve {
  strategyId: string;
  strategyName: string;
  dates: string[];
  values: number[];
}

export interface CorrelationResult {
  matrix: number[][]; // N×N Pearson correlations
  labels: string[];   // strategy names in matrix order
  ids: string[];      // strategy IDs in matrix order
}

export interface OptimizationResult {
  weights: number[];          // allocation per strategy (sums to 1)
  labels: string[];           // strategy names in order
  ids: string[];              // strategy IDs in order
  expectedReturn: number;     // annualised weighted CAGR %
  expectedVolatility: number; // annualised portfolio vol %
  expectedMaxDD: number;      // weighted max drawdown %
  portfolioSharpe: number;    // (return - rf) / vol
  diversificationBenefit: number; // % reduction in vol vs equal-weight
}

// ─── Helpers ──────────────────────────────────────────────────

/** Daily returns from an equity value series. */
function dailyReturns(values: number[]): number[] {
  const r: number[] = [];
  for (let i = 1; i < values.length; i++) {
    r.push(values[i] / values[i - 1] - 1);
  }
  return r;
}

/** Mean of an array. */
function mean(arr: number[]): number {
  let s = 0;
  for (let i = 0; i < arr.length; i++) s += arr[i];
  return s / arr.length;
}

/** Pearson correlation between two same-length arrays. */
function pearson(a: number[], b: number[]): number {
  const n = a.length;
  const ma = mean(a);
  const mb = mean(b);
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    const xa = a[i] - ma;
    const xb = b[i] - mb;
    num += xa * xb;
    da += xa * xa;
    db += xb * xb;
  }
  const denom = Math.sqrt(da * db);
  return denom === 0 ? 0 : num / denom;
}

/** Annualised covariance between two daily-return arrays (252 trading days). */
function annualisedCov(a: number[], b: number[]): number {
  const n = a.length;
  const ma = mean(a);
  const mb = mean(b);
  let s = 0;
  for (let i = 0; i < n; i++) {
    s += (a[i] - ma) * (b[i] - mb);
  }
  return (s / (n - 1)) * 252;
}

// ─── Date Alignment ──────────────────────────────────────────

/**
 * Align multiple equity curves to their common date range.
 * Returns aligned daily return arrays (same length, same dates).
 */
export function alignCurves(
  curves: EquityCurve[],
): { returns: number[][]; alignedDates: string[]; labels: string[]; ids: string[] } {
  // Build date→value maps for each curve
  const maps = curves.map((c) => {
    const m = new Map<string, number>();
    for (let i = 0; i < c.dates.length; i++) {
      m.set(c.dates[i], c.values[i]);
    }
    return m;
  });

  // Find common dates present in ALL curves
  const commonDates = [...maps[0].keys()].filter((d) =>
    maps.every((m) => m.has(d)),
  );
  commonDates.sort();

  // Build aligned equity arrays then compute daily returns
  const returns: number[][] = curves.map((_, ci) => {
    const equityAligned = commonDates.map((d) => maps[ci].get(d)!);
    return dailyReturns(equityAligned);
  });

  // Remove the first date (consumed by daily return calc)
  const alignedDates = commonDates.slice(1);

  return {
    returns,
    alignedDates,
    labels: curves.map((c) => c.strategyName),
    ids: curves.map((c) => c.strategyId),
  };
}

// ─── Correlation Matrix ──────────────────────────────────────

export function computeCorrelationMatrix(
  returns: number[][],
  labels: string[],
  ids: string[],
): CorrelationResult {
  const n = returns.length;
  const matrix: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    matrix[i][i] = 1;
    for (let j = i + 1; j < n; j++) {
      const r = pearson(returns[i], returns[j]);
      matrix[i][j] = r;
      matrix[j][i] = r;
    }
  }

  return { matrix, labels, ids };
}

// ─── Covariance Matrix ──────────────────────────────────────

function computeCovMatrix(returns: number[][]): number[][] {
  const n = returns.length;
  const cov: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = i; j < n; j++) {
      const c = annualisedCov(returns[i], returns[j]);
      cov[i][j] = c;
      cov[j][i] = c;
    }
  }
  return cov;
}

// ─── Portfolio Math ──────────────────────────────────────────

/** Portfolio variance given weights & covariance matrix. */
function portfolioVariance(w: number[], cov: number[][]): number {
  let v = 0;
  for (let i = 0; i < w.length; i++) {
    for (let j = 0; j < w.length; j++) {
      v += w[i] * w[j] * cov[i][j];
    }
  }
  return v;
}

/** Portfolio expected return given weights & per-asset returns. */
function portfolioReturn(w: number[], expectedReturns: number[]): number {
  let r = 0;
  for (let i = 0; i < w.length; i++) r += w[i] * expectedReturns[i];
  return r;
}

/** Generate a random weight vector that sums to 1, capped at maxWeight per asset. */
function randomWeights(n: number, maxWeight: number = 1): number[] {
  let cap = Math.min(maxWeight, 1);
  // If n * cap < 1, the cap is too tight to sum to 100% — raise to equal weight
  if (n * cap < 1) cap = 1 / n;
  if (cap >= 1) {
    // No cap — standard Dirichlet
    const raw: number[] = [];
    for (let i = 0; i < n; i++) raw.push(-Math.log(Math.random()));
    const s = raw.reduce((a, b) => a + b, 0);
    return raw.map((v) => v / s);
  }

  // Generate random weights then iteratively clip & redistribute excess
  const raw: number[] = [];
  for (let i = 0; i < n; i++) raw.push(-Math.log(Math.random()));
  const s = raw.reduce((a, b) => a + b, 0);
  const w = raw.map((v) => v / s);

  // Iterative clip-and-redistribute: clip over-cap weights, spread excess to under-cap weights
  for (let round = 0; round < 50; round++) {
    let excess = 0;
    let freeCount = 0;
    for (let i = 0; i < n; i++) {
      if (w[i] > cap) {
        excess += w[i] - cap;
        w[i] = cap;
      } else {
        freeCount++;
      }
    }
    if (excess < 1e-12) break; // all within cap
    // Distribute excess equally among uncapped weights
    const share = excess / Math.max(freeCount, 1);
    for (let i = 0; i < n; i++) {
      if (w[i] < cap) w[i] += share;
    }
  }

  // Final safety: hard-clamp and normalise
  for (let i = 0; i < n; i++) w[i] = Math.min(w[i], cap);
  const total = w.reduce((a, b) => a + b, 0);
  for (let i = 0; i < n; i++) w[i] /= total;
  return w;
}

/** Max drawdown of a value series (as negative percentage). */
function maxDrawdown(values: number[]): number {
  let peak = values[0];
  let worst = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > peak) peak = values[i];
    const dd = (values[i] - peak) / peak;
    if (dd < worst) worst = dd;
  }
  return worst * 100;
}

/** Build combined equity curve from individual return series and weights. */
export function combinedEquity(
  returns: number[][],
  weights: number[],
  initialValue: number = 100000,
): number[] {
  const len = returns[0].length;
  const equity: number[] = [initialValue];
  for (let d = 0; d < len; d++) {
    let dailyReturn = 0;
    for (let i = 0; i < weights.length; i++) {
      dailyReturn += weights[i] * returns[i][d];
    }
    equity.push(equity[equity.length - 1] * (1 + dailyReturn));
  }
  return equity;
}

// ─── Optimisation: Max Sharpe ────────────────────────────────

export function optimizeMaxSharpe(
  returns: number[][],
  expectedReturns: number[],
  labels: string[],
  ids: string[],
  riskFreeRate: number = 0,
  iterations: number = 50000,
  maxWeight: number = 1,
): OptimizationResult {
  const n = returns.length;
  const cov = computeCovMatrix(returns);

  let bestWeights = equalWeights(n);
  let bestSharpe = -Infinity;

  for (let iter = 0; iter < iterations; iter++) {
    const w = randomWeights(n, maxWeight);
    const ret = portfolioReturn(w, expectedReturns);
    const vol = Math.sqrt(portfolioVariance(w, cov));
    const sharpe = vol > 0 ? (ret - riskFreeRate) / vol : 0;
    if (sharpe > bestSharpe) {
      bestSharpe = sharpe;
      bestWeights = w;
    }
  }

  return buildResult(bestWeights, returns, expectedReturns, cov, labels, ids, riskFreeRate);
}

// ─── Optimisation: Max CAGR ──────────────────────────────────

export function optimizeMaxCAGR(
  returns: number[][],
  expectedReturns: number[],
  labels: string[],
  ids: string[],
  riskFreeRate: number = 0,
  iterations: number = 50000,
  maxWeight: number = 1,
): OptimizationResult {
  const n = returns.length;
  const cov = computeCovMatrix(returns);

  let bestWeights = equalWeights(n);
  let bestReturn = -Infinity;

  for (let iter = 0; iter < iterations; iter++) {
    const w = randomWeights(n, maxWeight);
    const ret = portfolioReturn(w, expectedReturns);
    if (ret > bestReturn) {
      bestReturn = ret;
      bestWeights = w;
    }
  }

  return buildResult(bestWeights, returns, expectedReturns, cov, labels, ids, riskFreeRate);
}

// ─── Optimisation: Min MaxDD ─────────────────────────────────

export function optimizeMinDrawdown(
  returns: number[][],
  expectedReturns: number[],
  labels: string[],
  ids: string[],
  riskFreeRate: number = 0,
  iterations: number = 30000,
  maxWeight: number = 1,
): OptimizationResult {
  const n = returns.length;
  const cov = computeCovMatrix(returns);

  let bestWeights = equalWeights(n);
  let bestDD = -Infinity; // max drawdown is negative, closest to 0 is best

  for (let iter = 0; iter < iterations; iter++) {
    const w = randomWeights(n, maxWeight);
    const eq = combinedEquity(returns, w);
    const dd = maxDrawdown(eq);
    if (dd > bestDD) {
      bestDD = dd;
      bestWeights = w;
    }
  }

  // Refine: do a local search around the best weights found
  let cap = Math.min(maxWeight, 1);
  if (n * cap < 1) cap = 1 / n;
  for (let iter = 0; iter < 10000; iter++) {
    // Perturb best weights slightly
    const w = bestWeights.map((bw) => Math.max(0, bw + (Math.random() - 0.5) * 0.1));
    const s = w.reduce((a, b) => a + b, 0);
    for (let i = 0; i < n; i++) w[i] /= s;
    // Clip-redistribute to respect cap
    for (let round = 0; round < 20; round++) {
      let excess = 0;
      let freeCount = 0;
      for (let i = 0; i < n; i++) {
        if (w[i] > cap) { excess += w[i] - cap; w[i] = cap; } else { freeCount++; }
      }
      if (excess < 1e-12) break;
      const share = excess / Math.max(freeCount, 1);
      for (let i = 0; i < n; i++) { if (w[i] < cap) w[i] += share; }
    }
    const eq = combinedEquity(returns, w);
    const dd = maxDrawdown(eq);
    if (dd > bestDD) {
      bestDD = dd;
      bestWeights = [...w];
    }
  }

  return buildResult(bestWeights, returns, expectedReturns, cov, labels, ids, riskFreeRate);
}

// ─── Shared Result Builder ───────────────────────────────────

function equalWeights(n: number): number[] {
  return new Array(n).fill(1 / n);
}

function buildResult(
  weights: number[],
  returns: number[][],
  expectedReturns: number[],
  cov: number[][],
  labels: string[],
  ids: string[],
  riskFreeRate: number,
): OptimizationResult {
  const ret = portfolioReturn(weights, expectedReturns);
  const vol = Math.sqrt(portfolioVariance(weights, cov));
  // expectedReturns & riskFreeRate are in % (e.g. 45.2), vol is decimal (e.g. 0.15)
  // Convert vol to % for consistent Sharpe calculation
  const volPct = vol * 100;
  const sharpe = volPct > 0 ? (ret - riskFreeRate) / volPct : 0;

  // Combined equity for drawdown
  const eq = combinedEquity(returns, weights);
  const dd = maxDrawdown(eq);

  // Diversification benefit: compare portfolio vol to weighted-average individual vol
  const individualVols = returns.map((r) => {
    const m = mean(r);
    let s = 0;
    for (let i = 0; i < r.length; i++) s += (r[i] - m) ** 2;
    return Math.sqrt((s / (r.length - 1)) * 252);
  });
  const weightedAvgVol = weights.reduce((sum, w, i) => sum + w * individualVols[i], 0);
  const divBenefit = weightedAvgVol > 0 ? ((weightedAvgVol - vol) / weightedAvgVol) * 100 : 0;

  return {
    weights,
    labels,
    ids,
    expectedReturn: ret,
    expectedVolatility: volPct,
    expectedMaxDD: dd,
    portfolioSharpe: sharpe,
    diversificationBenefit: divBenefit,
  };
}

// ─── High-Level API ──────────────────────────────────────────

export function optimize(
  mode: OptimizationMode,
  returns: number[][],
  expectedReturns: number[],
  labels: string[],
  ids: string[],
  riskFreeRate: number = 0,
  maxWeight: number = 1,
): OptimizationResult {
  switch (mode) {
    case "max_sharpe":
      return optimizeMaxSharpe(returns, expectedReturns, labels, ids, riskFreeRate, 50000, maxWeight);
    case "max_cagr":
      return optimizeMaxCAGR(returns, expectedReturns, labels, ids, riskFreeRate, 50000, maxWeight);
    case "min_drawdown":
      return optimizeMinDrawdown(returns, expectedReturns, labels, ids, riskFreeRate, 30000, maxWeight);
  }
}
